<script setup lang="ts">
/**
 * NotFoundSuggestions — suggestions de contenus ACTIFS affichées sous l'état
 * d'erreur « n'existe pas / plus disponible » des pages publiques (/c/, /a/,
 * /b/, /associations). Idée Karim (2026-08-01) : plutôt qu'un cul-de-sac,
 * proposer des cagnottes/événements/adhésions/assos en cours.
 *
 * Source = les MÊMES endpoints publics que /recherche (listes actives), car
 * l'endpoint « related/:slug » renvoie [] quand le slug n'existe pas. Les
 * cartes réutilisées = celles de /recherche (cohérence visuelle).
 *
 * NB SEO : la page hôte renvoie un VRAI statut 404 (cf. pages) — ces
 * suggestions sont pour l'HUMAIN ; Google, lui, voit le 404.
 */
import type {
  PublicCampaign,
  PublicEvent,
  PublicPlan,
  PublicOrganization,
} from "~/composables/useRechercheData";

const { t } = useI18n();

const props = defineProps<{
  type: "cagnottes" | "evenements" | "adhesions" | "associations";
  /**
   * Variante « asso indisponible » : titre « Découvrez d'autres … » + accroche.
   * Sur une page suspendue, le titre par défaut (« Ces événements sont
   * ouverts ») tombe à plat — le visiteur vient de se voir refuser l'accès, on
   * lui propose explicitement autre chose.
   */
  alternative?: boolean;
}>();

const config = useRuntimeConfig();
const apiBaseUrl = config.public.apiBaseUrl;

const ENDPOINTS: Record<typeof props.type, string> = {
  cagnottes: `${apiBaseUrl}/api/campaigns/public`,
  evenements: `${apiBaseUrl}/api/organizations/public/all-events`,
  adhesions: `${apiBaseUrl}/api/organizations/public/all-membership-plans`,
  associations: `${apiBaseUrl}/api/organizations/public`,
};

// Clés i18n, traduites à l'affichage (computed) pour suivre la langue.
const TITLES: Record<typeof props.type, string> = {
  cagnottes: "partage.suggestions404.titreCagnottes",
  evenements: "partage.suggestions404.titreEvenements",
  adhesions: "partage.suggestions404.titreAdhesions",
  associations: "partage.suggestions404.titreAssociations",
};

const ALT_TITLES: Record<typeof props.type, string> = {
  cagnottes: "partage.suggestions404.autresCagnottes",
  evenements: "partage.suggestions404.autresEvenements",
  adhesions: "partage.suggestions404.autresAdhesions",
  associations: "association.suggestions.associationsTitre",
};

const ALT_SUBTITLES: Record<typeof props.type, string> = {
  cagnottes: "partage.suggestions404.accrocheCagnottes",
  evenements: "partage.suggestions404.accrocheEvenements",
  adhesions: "partage.suggestions404.accrocheAdhesions",
  associations: "partage.suggestions404.accrocheAssociations",
};

const heading = computed(() =>
  t(props.alternative ? ALT_TITLES[props.type] : TITLES[props.type]),
);
const subheading = computed(() =>
  props.alternative ? t(ALT_SUBTITLES[props.type]) : null,
);

interface AnyList {
  data?: (PublicCampaign | PublicEvent | PublicPlan | PublicOrganization)[];
}

// SSR + client. Les descriptions HTML complètes ne sont jamais affichées par
// les cartes → on les vide pour ne pas gonfler le payload SSR (même précaution
// que /recherche, audit SEO 2026-07-11).
const { data } = await useFetch<AnyList>(ENDPOINTS[props.type], {
  key: `notfound-suggestions-${props.type}`,
  query: { limit: 6, offset: 0 },
  transform: (res: AnyList) => ({
    data: (res.data ?? []).slice(0, 6).map((item) => ({
      ...item,
      ...("description" in item ? { description: null } : {}),
    })),
  }),
});

const items = computed(() => data.value?.data ?? []);

// Carousel horizontal (même pattern que CampaignRelatedSection) : scroll natif
// + snap + flèches desktop. Remplace la grille 3 colonnes (UX « mur de cartes »
// sous l'état 404, retour Karim 2026-08-05).
const scroller = ref<HTMLElement | null>(null);
function scrollByDir(dir: "left" | "right") {
  const el = scroller.value;
  if (!el) return;
  const delta = Math.round(el.clientWidth * 0.8) * (dir === "left" ? -1 : 1);
  el.scrollBy({ left: delta, behavior: "smooth" });
}
</script>

<template>
  <section v-if="items.length" class="max-w-6xl mx-auto px-4 pb-16 w-full">
    <div class="flex items-center justify-between gap-4 mb-6">
      <div class="min-w-0">
        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {{ heading }}
        </h2>
        <p v-if="subheading" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {{ subheading }}
        </p>
      </div>
      <!-- Flèches desktop (le tactile scrolle nativement) -->
      <div class="hidden sm:flex items-center gap-2">
        <button
          type="button"
          class="size-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center text-gray-700"
          :aria-label="$t('association.suggestions.defilerGauche')"
          @click="scrollByDir('left')"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
        </button>
        <button
          type="button"
          class="size-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center text-gray-700"
          :aria-label="$t('association.suggestions.defilerDroite')"
          @click="scrollByDir('right')"
        >
          <UIcon name="i-lucide-arrow-right" class="size-4" />
        </button>
      </div>
    </div>

    <!-- Carousel horizontal : scroll natif + snap (pattern CampaignRelatedSection) -->
    <div
      ref="scroller"
      class="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide"
    >
      <template v-if="type === 'cagnottes'">
        <div
          v-for="c in items"
          :key="(c as PublicCampaign).id"
          class="shrink-0 snap-start w-[85vw] max-w-[320px] sm:w-[340px] sm:max-w-none"
        >
          <RechercheCagnotteCard :campaign="c as PublicCampaign" />
        </div>
      </template>
      <template v-else-if="type === 'evenements'">
        <div
          v-for="e in items"
          :key="(e as PublicEvent).id"
          class="shrink-0 snap-start w-[85vw] max-w-[320px] sm:w-[340px] sm:max-w-none"
        >
          <RechercheEventCard :event="e as PublicEvent" />
        </div>
      </template>
      <template v-else-if="type === 'adhesions'">
        <div
          v-for="p in items"
          :key="(p as PublicPlan).id"
          class="shrink-0 snap-start w-[85vw] max-w-[320px] sm:w-[340px] sm:max-w-none"
        >
          <RechercheAdhesionCard :plan="p as PublicPlan" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="o in items"
          :key="(o as PublicOrganization).id"
          class="shrink-0 snap-start w-[85vw] max-w-[320px] sm:w-[340px] sm:max-w-none"
        >
          <RechercheOrganizationCard :org="o as PublicOrganization" />
        </div>
      </template>
    </div>
  </section>
</template>

<style scoped>
/* Masque la scrollbar du carousel (même convention que CagnottesHero /
   CampaignRelatedSection) — le scroll reste fonctionnel (molette, tactile,
   flèches). */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
